import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { supabase } from "../supabase";
import { C, MOIS, mono } from "../config/theme";
import { fmt, fmtK } from "../utils/format";
import KpiCard from "../components/KpiCard";
import FilterBar from "../components/FilterBar";
import MiniTable from "../components/MiniTable";
import DrillDown from "../components/DrillDown";
import StatusMessage from "../components/StatusMessage";
import CustomTooltip from "../components/CustomTooltip";

/* ─── Labels MO ─── */
const FONCTION_LABELS = {
  compagnon:    "Compagnon",
  chef_equipe:  "Chef d'équipe",
  conducteur:   "Conducteur",
  apprenti:     "Apprenti",
  charge_aff:   "Chargé d'affaires",
  manoeuvre:    "Manœuvre",
};

const TYPE_CHARGE_LABELS = {
  interne:  "Interne",
  interim:  "Intérim",
  externe:  "Externe",
};

const TYPE_CHARGE_COLORS = {
  interne: { bg: "#EFF6FF", color: C.blue },
  interim: { bg: "#FFFBEB", color: C.accent },
  externe: { bg: "#F3E8FF", color: "#8B5CF6" },
};

function TypeChargeBadge({ type }) {
  const v = TYPE_CHARGE_COLORS[type] || { bg: C.bg, color: C.slate };
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
      background: v.bg, color: v.color, whiteSpace: "nowrap",
    }}>
      {TYPE_CHARGE_LABELS[type] || type}
    </span>
  );
}

function SectionTitle({ children, color = C.navy }) {
  return (
    <div style={{
      fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px",
      color, margin: "24px 0 12px", paddingBottom: 8, borderBottom: `2px solid ${color}20`,
    }}>
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   PAGE RH — Dashboard main d'œuvre
   ═══════════════════════════════════════════════════════════════════════════════ */
export default function PageRH() {
  /* ─── Filtres ─── */
  const [annee, setAnnee] = useState(null);
  const [mois, setMois] = useState(null); // null = toute l'année
  const [filAgence, setFilAgence] = useState("");

  const [filtresData, setFiltresData] = useState(null);
  const [global, setGlobal] = useState(null);
  const [tableaux, setTableaux] = useState(null);
  const [graphiques, setGraphiques] = useState(null);

  const [loadingFiltres, setLoadingFiltres] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);

  /* ─── Params RPC ─── */
  const paramsGlobal = useMemo(() => ({
    p_annee: annee,
    ...(mois != null ? { p_mois: mois } : {}),
    ...(filAgence ? { p_agence_id: filAgence } : {}),
  }), [annee, mois, filAgence]);

  // graphiques ne prend que (annee, agence) — pas de mois
  const paramsGraphiques = useMemo(() => ({
    p_annee: annee,
    ...(filAgence ? { p_agence_id: filAgence } : {}),
  }), [annee, filAgence]);

  /* ─── Init filtres ─── */
  useEffect(() => {
    async function loadFiltres() {
      setLoadingFiltres(true);
      const { data, error: err } = await supabase.rpc("bv_rpc_ca_filtres");
      if (err) {
        console.error("Erreur filtres RH:", err);
        setError("Impossible de charger les filtres.");
        setLoadingFiltres(false);
        return;
      }
      setFiltresData(data);
      if (data?.annees?.length > 0) setAnnee(data.annees[0]);
      setLoadingFiltres(false);
    }
    loadFiltres();
  }, []);

  /* ─── Chargement données ─── */
  useEffect(() => {
    if (!annee) return;
    async function loadData() {
      setLoadingData(true);
      try {
        const [r1, r2, r3] = await Promise.all([
          supabase.rpc("bv_rpc_rh_global", paramsGlobal),
          supabase.rpc("bv_rpc_rh_tableaux", paramsGlobal),
          supabase.rpc("bv_rpc_rh_graphiques", paramsGraphiques),
        ]);
        if (r1.data) setGlobal(r1.data);
        if (r2.data) setTableaux(r2.data);
        if (r3.data) setGraphiques(r3.data);
        [r1, r2, r3].forEach((r, i) => {
          if (r.error) console.error(`Erreur RPC RH #${i + 1}:`, r.error);
        });
      } catch (err) {
        console.error("Erreur chargement RH:", err);
        setError("Erreur lors du chargement des données.");
      } finally {
        setLoadingData(false);
      }
    }
    loadData();
  }, [annee, paramsGlobal, paramsGraphiques]);

  /* ─── Donut répartition heures ─── */
  const donutHeures = useMemo(() => {
    if (!global) return [];
    const total = (global.heures_travaillees || 0) + (global.heures_absence || 0) + (global.heures_intemperies || 0);
    if (total === 0) return [];
    return [
      { name: "Travaillées",  value: global.heures_travaillees || 0, color: C.green },
      { name: "Absences",     value: global.heures_absence || 0,     color: C.red },
      { name: "Intempéries",  value: global.heures_intemperies || 0, color: C.blue },
    ].map((r) => ({ ...r, pct: ((r.value / total) * 100).toFixed(1) }));
  }, [global]);

  /* ─── Évolution mensuelle — compléter 12 mois ─── */
  const evolutionMois = useMemo(() => {
    const map = new Map();
    (graphiques?.evolution_mensuelle || []).forEach((r) => map.set(r.mois, r));
    const out = [];
    for (let m = 1; m <= 12; m++) {
      const r = map.get(m) || {};
      out.push({
        mois: MOIS[m],
        cout_debourse:    r.cout_debourse || 0,
        heures_internes:  r.heures_internes || 0,
        heures_interim:   r.heures_interim || 0,
        heures_travaillees: r.heures_travaillees || 0,
      });
    }
    return out;
  }, [graphiques]);

  /* ─── Colonnes drill-down salariés ─── */
  const colonnesSalaries = useMemo(() => [
    { key: "salarie", label: "Salarié", bold: true },
    { key: "fonction", label: "Fonction",
      render: (r) => FONCTION_LABELS[r.fonction] || r.fonction },
    { key: "type_charge", label: "Type",
      render: (r) => <TypeChargeBadge type={r.type_charge} /> },
    { key: "cout_horaire_debourse", label: "Coût horaire", align: "right", mono: true,
      render: (r) => `${Number(r.cout_horaire_debourse || 0).toFixed(2).replace(".", ",")} €` },
    { key: "heures_travaillees", label: "H. travaillées", align: "right", mono: true,
      render: (r) => Number(r.heures_travaillees || 0).toLocaleString("fr-FR") },
    { key: "heures_absence", label: "H. absence", align: "right", mono: true,
      render: (r) => Number(r.heures_absence || 0).toLocaleString("fr-FR") },
    { key: "heures_intemperies", label: "H. intempéries", align: "right", mono: true,
      render: (r) => Number(r.heures_intemperies || 0).toLocaleString("fr-FR") },
    { key: "heures_totales", label: "H. totales", align: "right", mono: true, bold: true,
      render: (r) => Number(r.heures_totales || 0).toLocaleString("fr-FR") },
    { key: "cout_debourse", label: "Coût débourse", align: "right", mono: true, bold: true,
      render: (r) => fmt(r.cout_debourse) },
  ], []);

  /* ═══════════════════════════════════════════════════════════════════════════
     RENDU
     ═══════════════════════════════════════════════════════════════════════════ */
  if (loadingFiltres) {
    return <div style={{ padding: "40px 0", textAlign: "center", color: C.slate }}>Chargement…</div>;
  }
  if (error) {
    return <StatusMessage type="error" message={error} action={{ label: "Réessayer", onClick: () => window.location.reload() }} />;
  }
  if (!filtresData?.annees?.length) {
    return <StatusMessage type="empty" message="Aucune donnée disponible." detail="Les données n'ont pas encore été synchronisées depuis l'ERP." />;
  }
  if (!annee) return null;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.text }}>RH / Main d'œuvre</h1>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: C.slate }}>
          Effectifs, heures pointées, coûts MO et taux d'absentéisme
        </p>
      </div>

      {/* ─── Filtres ─── */}
      <FilterBar
        annees={filtresData.annees} annee={annee} setAnnee={setAnnee}
        showPeriode={false}
        showMoisUnique={true} mois={mois} setMois={setMois}
        showClient={false}
        showChargeAffaires={false}
        showConducteur={false}
        activites={[]}
        agences={filtresData.agences || []}
        filAgence={filAgence} setFilAgence={setFilAgence}
      />

      {loadingData && (
        <div style={{ textAlign: "center", padding: "12px 0", color: C.slate, fontSize: 13, marginBottom: 8 }}>
          Actualisation des données…
        </div>
      )}

      {/* ═══ KPI LIGNE 1 — Effectifs ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, marginBottom: 12 }}>
        <KpiCard label="Effectif total" value={global?.effectif_total || 0} color={C.navy} isCurrency={false} />
        <KpiCard label="Effectif interne" value={global?.effectif_interne || 0} color={C.blue} isCurrency={false} />
        <KpiCard label="Effectif intérim" value={global?.effectif_interim || 0} color={C.accent} isCurrency={false} />
        <KpiCard label="Effectif prêté" value={global?.effectif_prete || 0} color={C.slate} isCurrency={false} />
      </div>

      {/* ═══ KPI LIGNE 2 — Ratio, heures, coûts globaux ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, marginBottom: 12 }}>
        <KpiCard
          label="Ratio intérim"
          value={global?.ratio_interim || 0}
          color={(global?.ratio_interim || 0) > 30 ? C.red : (global?.ratio_interim || 0) > 15 ? C.accent : C.green}
          suffix="%"
        />
        <KpiCard
          label="Heures pointées"
          value={global?.heures_pointees || 0}
          color={C.blue}
          isCurrency={false}
        />
        <KpiCard label="Coût MO total" value={global?.cout_mo_total || 0} color={C.accent} />
        <KpiCard
          label="Coût horaire débourse"
          value={global?.cout_horaire_moyen_debourse || 0}
          color={C.accent}
          isCurrency={false}
          suffix=" €/h"
        />
      </div>

      {/* ═══ KPI LIGNE 3 — Coût horaire revient + taux absentéisme ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, marginBottom: 16 }}>
        <KpiCard
          label="Coût horaire revient"
          value={global?.cout_horaire_moyen_revient || 0}
          color="#8B5CF6"
          isCurrency={false}
          suffix=" €/h"
        />
        <KpiCard
          label="Taux d'absence"
          value={global?.taux_absence || 0}
          color={(global?.taux_absence || 0) > 5 ? C.red : (global?.taux_absence || 0) > 2 ? C.accent : C.green}
          suffix="%"
        />
        <KpiCard
          label="Taux d'intempéries"
          value={global?.taux_intemperies || 0}
          color={(global?.taux_intemperies || 0) > 5 ? C.red : (global?.taux_intemperies || 0) > 2 ? C.accent : C.blue}
          suffix="%"
        />
      </div>

      {/* ═══ GRAPHIQUES ═══ */}
      <SectionTitle color={C.blue}>Vision graphique</SectionTitle>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16, marginBottom: 16 }}>
        {/* Donut répartition heures */}
        <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: "16px 20px", boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: C.green, marginBottom: 8 }}>
            Répartition des heures
          </div>
          {donutHeures.length > 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <ResponsiveContainer width={140} height={140}>
                <PieChart>
                  <Pie data={donutHeures} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={36} outerRadius={60} strokeWidth={2} stroke={C.surface}>
                    {donutHeures.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => `${Number(v).toLocaleString("fr-FR")} h`} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ flex: 1, fontSize: 11 }}>
                {donutHeures.map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "3px 0", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: item.color }} />
                      <span style={{ color: C.text }}>{item.name}</span>
                    </div>
                    <span style={{ fontFamily: mono, fontWeight: 600, color: C.slate, whiteSpace: "nowrap" }}>
                      {Number(item.value).toLocaleString("fr-FR")} h ({item.pct}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ padding: 24, textAlign: "center", color: C.slateLight, fontSize: 12 }}>Aucune heure pointée</div>
          )}
        </div>

        {/* Évolution coût MO mensuel */}
        <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: "16px 20px", boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: C.accent, marginBottom: 8 }}>
            Coût MO mensuel — {annee}
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={evolutionMois}>
              <XAxis dataKey="mois" fontSize={10} tick={{ fill: C.slate }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="cout_debourse" name="Coût débourse" stroke={C.accent} strokeWidth={2.5} dot={{ r: 4, fill: C.accent }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bar chart interne vs intérim par mois */}
      <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: "16px 20px", boxShadow: "0 1px 8px rgba(0,0,0,0.05)", marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: C.blue, marginBottom: 8 }}>
          Heures internes vs intérim — {annee}
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={evolutionMois} barGap={2}>
            <XAxis dataKey="mois" fontSize={10} tick={{ fill: C.slate }} axisLine={false} tickLine={false} />
            <YAxis fontSize={10} tick={{ fill: C.slate }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="square"
              wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
            />
            <Bar dataKey="heures_internes" name="Interne" stackId="h" fill={C.blue} radius={[0, 0, 0, 0]} maxBarSize={32} />
            <Bar dataKey="heures_interim" name="Intérim" stackId="h" fill={C.accent} radius={[3, 3, 0, 0]} maxBarSize={32} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ═══ TABLEAUX : par fonction + par chantier ═══ */}
      <SectionTitle color={C.accent}>Répartition</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16, marginBottom: 16 }}>
        <MiniTable
          title="Par fonction"
          color={C.blue}
          headers={[
            { key: "fonction", label: "Fonction", bold: true,
              render: (r) => FONCTION_LABELS[r.fonction] || r.fonction,
              total: () => "Total" },
            { key: "effectif", label: "Effectif", align: "right", mono: true,
              total: (rows) => rows.reduce((a, r) => a + (r.effectif || 0), 0) },
            { key: "heures", label: "Heures", align: "right", mono: true, bold: true,
              render: (r) => Number(r.heures || 0).toLocaleString("fr-FR"),
              total: (rows) => Number(rows.reduce((a, r) => a + (r.heures || 0), 0)).toLocaleString("fr-FR") },
            { key: "cout", label: "Coût", align: "right", mono: true,
              render: (r) => fmt(r.cout),
              total: (rows) => fmt(rows.reduce((a, r) => a + (r.cout || 0), 0)) },
          ]}
          rows={tableaux?.par_fonction || []}
        />

        <MiniTable
          title="Par chantier"
          color={C.accent}
          headers={[
            { key: "code_erp", label: "Code", bold: true, mono: true, total: () => "" },
            { key: "chantier", label: "Chantier", maxW: 220, total: () => "Total" },
            { key: "heures_travaillees", label: "Travaillées", align: "right", mono: true,
              render: (r) => Number(r.heures_travaillees || 0).toLocaleString("fr-FR"),
              total: (rows) => Number(rows.reduce((a, r) => a + (r.heures_travaillees || 0), 0)).toLocaleString("fr-FR") },
            { key: "heures_absence", label: "Absence", align: "right", mono: true,
              render: (r) => Number(r.heures_absence || 0).toLocaleString("fr-FR"),
              total: (rows) => Number(rows.reduce((a, r) => a + (r.heures_absence || 0), 0)).toLocaleString("fr-FR") },
            { key: "heures_intemperies", label: "Intemp.", align: "right", mono: true,
              render: (r) => Number(r.heures_intemperies || 0).toLocaleString("fr-FR"),
              total: (rows) => Number(rows.reduce((a, r) => a + (r.heures_intemperies || 0), 0)).toLocaleString("fr-FR") },
            { key: "heures_totales", label: "Totales", align: "right", mono: true, bold: true,
              render: (r) => Number(r.heures_totales || 0).toLocaleString("fr-FR"),
              total: (rows) => Number(rows.reduce((a, r) => a + (r.heures_totales || 0), 0)).toLocaleString("fr-FR") },
            { key: "cout", label: "Coût", align: "right", mono: true,
              render: (r) => fmtK(r.cout),
              total: (rows) => fmtK(rows.reduce((a, r) => a + (r.cout || 0), 0)) },
          ]}
          rows={tableaux?.par_chantier || []}
        />
      </div>

      {/* ═══ DETAIL PAR SALARIÉ (DrillDown) ═══ */}
      <SectionTitle color="#8B5CF6">Détail par salarié</SectionTitle>
      {(tableaux?.par_salarie || []).length > 0 ? (
        <DrillDown
          title={`Heures et coûts par salarié (${tableaux.par_salarie.length})`}
          color="#8B5CF6"
          columns={colonnesSalaries}
          rows={tableaux.par_salarie}
        />
      ) : (
        <StatusMessage type="empty" message="Aucun pointage sur cette période." />
      )}

      {/* ═══ INTÉRIM PAR CHANTIER (conditionnel) ═══ */}
      {(tableaux?.interim_par_chantier || []).length > 0 && (
        <>
          <SectionTitle color={C.accent}>Intérim par chantier</SectionTitle>
          <MiniTable
            title={`Heures et coûts intérim (${tableaux.interim_par_chantier.length})`}
            color={C.accent}
            headers={[
              { key: "code_erp", label: "Code", bold: true, mono: true, total: () => "" },
              { key: "chantier", label: "Chantier", maxW: 320, total: () => "Total" },
              { key: "heures", label: "Heures", align: "right", mono: true, bold: true,
                render: (r) => Number(r.heures || 0).toLocaleString("fr-FR"),
                total: (rows) => Number(rows.reduce((a, r) => a + (r.heures || 0), 0)).toLocaleString("fr-FR") },
              { key: "cout", label: "Coût", align: "right", mono: true, bold: true,
                render: (r) => fmt(r.cout),
                total: (rows) => fmt(rows.reduce((a, r) => a + (r.cout || 0), 0)) },
            ]}
            rows={tableaux.interim_par_chantier}
          />
        </>
      )}
    </div>
  );
}
